import { BaseUserProfile } from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import {
  Platform, ScrollView, StyleSheet, View,
} from 'react-native';
import { connect } from 'react-redux';
import DollarIcon from '../assets/icons/currency-dollar-circle.svg';
import FlagIcon from '../assets/icons/flag.svg';
import HelpIcon from '../assets/icons/help-circle.svg';
import LogoutIcon from '../assets/icons/log-out.svg';
import RefreshIcon from '../assets/icons/refresh.svg';
import SettingsIcon from '../assets/icons/settings.svg';
import StrengthIcon from '../assets/icons/strength.svg';
import UserIcon from '../assets/icons/user-simple.svg';
import UsersPlusIcon from '../assets/icons/users-plus.svg';
import WatchIcon from '../assets/icons/watch-outline.svg';
import { hasAccessToAStudioGymSelector } from '../auth';
import { getConfig } from '../config/index';
import { StrengthAssessmentWizard } from '../StrengthAssessment';
import Theme from '../theme';
import { showMessage } from '../ui';
import { toggleStrengthAssessmentModal } from '../ui/redux';
import { LogoutHelper, Share, UserDataConsumer } from '../utils';
import { defaultScale } from '../utils/scaling';
import { MenuIcon, ProfileDisplay, ProfileNavigationTile } from './components';

const isWeb = Platform.OS === 'web';

function ProfileScreenComponent({
  user, navigation, t, hasAccessToAStudioGym, openStrengthAssessmentModal,
}) {
  const { userServiceClient } = user;
  const logout = async () => {
    await LogoutHelper();
  };

  const menuIconProps = {
    color: Theme.GREY_DARK_MEDIUM,
    width: defaultScale(24),
    height: defaultScale(24),
  };

  const isUserPartOfFamilyPlan = BaseUserProfile.isPartOfFamilyPlan(user.userProfile);
  const { isElite } = getConfig().variant;

  const screens = [
    ...isUserPartOfFamilyPlan && !isWeb ? [{
      title: 'Switch Member',
      navigate: () => navigation.navigate('SwitchMember'),
      icon: <RefreshIcon {...menuIconProps} />,
    }] : [],
    ...!isWeb ? [{
      title: 'Connected Apps and Devices',
      navigate: () => navigation.navigate('ConnectedApps'),
      icon: <WatchIcon {...menuIconProps} />,
    }] : [],
    {
      title: 'Goal and Skill Level',
      navigate: () => navigation.navigate('Goals'),
      icon: <FlagIcon {...menuIconProps} />,
    },
    ...!isWeb ? [{
      title: 'Strength Assessment',
      navigate: () => openStrengthAssessmentModal(),
      icon: (
        <View style={styles.strengthIconContainer}>
          <StrengthIcon color={Theme.GREY_DARK_MEDIUM} width={defaultScale(22)} height={defaultScale(22)} />
        </View>
      ),
    }] : [],
    {
      title: 'Personal Settings',
      navigate: () => navigation.navigate('PersonalSettings'),
      icon: <UserIcon {...menuIconProps} />,
    }, {
      title: 'Preferences',
      navigate: () => navigation.navigate('Preferences'),
      icon: <SettingsIcon {...menuIconProps} />,
    },
    ...!hasAccessToAStudioGym ? [{
      title: 'Friends',
      navigate: async () => {
        const { link, personalisedHeader } = await userServiceClient.getInviteFriendsLink();
        await shareInviteFriendLink(t, link, user.userProfile.fullname, personalisedHeader);
      },
      icon: <UsersPlusIcon {...menuIconProps} />,
    }] : [],
    ...hasAccessToAStudioGym ? [{
      title: 'Friends',
      navigate: () => navigation.navigate('WorkoutPartners'),
      icon: <UsersPlusIcon {...menuIconProps} />,
    }] : [],
    {
      title: 'Billing',
      navigate: () => navigation.navigate('Billing'),
      icon: <DollarIcon {...menuIconProps} />,
    },
    {
      title: 'Help',
      navigate: () => navigation.navigate('Help'),
      icon: <HelpIcon {...menuIconProps} />,
    },
    ...isElite ? [{
      title: 'Wi-Fi Setup',
      navigate: () => navigation.navigate('Wifi', { navigationSource: 'ProfileScreen' }),
      // smaller icon, with vertical margin to keep height (1.2, because 1 is just to small)
      icon: (
        <MenuIcon name='ios-wifi-outline' size={defaultScale(24)} customStyle={{ marginVertical: defaultScale(1.2) }} />
      ),
    }] : [],
    {
      title: isElite && isUserPartOfFamilyPlan ? 'End My Session' : 'Signout',
      navigate: isElite && isUserPartOfFamilyPlan ? () => navigation.navigate('confirmSessionEnding') : logout,
      icon: <LogoutIcon {...menuIconProps} />,
    }];

  return (
    <View style={styles.container}>
      <ProfileDisplay userProfile={user.userProfile} navigation={navigation} />

      <ScrollView>
        {screens.map((screen) => (
          <ProfileNavigationTile
            key={screen.title}
            title={screen.title}
            onPress={screen.navigate}
            icon={screen.icon}
          />
        ))}
      </ScrollView>
      <StrengthAssessmentWizard userProfile={user.userProfile} />

    </View>
  );
}

const shareInviteFriendLink = async (t, link, fullname, personalisedHeader) => {
  const messageDetails = {
    personalisedHeader,
    fullname: fullname.toUpperCase(),
  };

  const result = await Share.share({
    message: `${t('home:inviteFriends', messageDetails)}\n${link}`,
    title: t('home:inviteUserToJoinFitillion', { personalisedHeader }),
  });

  if (result.action === Share.sharedAction) {
    showMessage(t('home:inviteHasBeenSent'));
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: defaultScale(10),
    paddingBottom: 0,
    borderTopWidth: defaultScale(1),
    backgroundColor: Theme.GREY_LIGHT,
    borderTopColor: Theme.GREY_LIGHT,
    height: '100%',
  },
  strengthIconContainer: {
    width: defaultScale(24),
    height: defaultScale(24),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const mapStateToProps = (state) => ({
  hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  openStrengthAssessmentModal: () => dispatch(toggleStrengthAssessmentModal(true)),
});

export const ProfileScreen = UserDataConsumer(
  translate(['home'], { wait: true })(
    connect(mapStateToProps, mapDispatchToProps)(ProfileScreenComponent),
  ),
);
